/**
 * Runtime configuration loader for hub-ui
 * Determines connection mode (local/cloud/electron/capacitor) and loads appropriate config
 */

export interface RuntimeConfig {
  mode: 'local' | 'cloud' | 'electron' | 'capacitor';
  apiUrl: string;
  wsUrl: string;
  cloudApiUrl: string;
  cloudWsUrl: string;
  /** True when otto-ui is embedded inside otto-web at /app */
  embeddedInOttoWeb?: boolean;
  /**
   * This origin answered as a hub: the page was served BY the hub it is looking
   * at, on a hostname the localhost test cannot see as one (card `t_6d25f68a`).
   * `apiUrl`/`wsUrl` are then this origin's own, and the storage restore is
   * skipped — see `isHubServedOrigin`.
   */
  hubServed?: boolean;
}

let config: RuntimeConfig | null = null;
const ELECTRON_LOCAL_PROTOCOL = 'otto-local';
const ACTIVE_HUB_IP_KEY = 'otto_active_hub_ip';
const ACTIVE_HUB_PORT_KEY = 'otto_active_hub_port';
const ACTIVE_HUB_SECURE_KEY = 'otto_active_hub_secure';

function getStoredActiveHubSecure(): boolean {
  try {
    return localStorage.getItem(ACTIVE_HUB_SECURE_KEY) === 'true';
  } catch {
    return false;
  }
}

// ── Per-tab active hub ──────────────────────────────────────────────────────
// localStorage is shared by every Electron tab, so the "active hub" written by
// one tab silently rewrites the other hub tabs' restore target (reloading the
// Rožna tab showed Dev). sessionStorage is per window: each tab remembers the
// hub IT is connected to and comes back to that server on reload.
const TAB_HUB_KEY = 'otto_tab_active_hub';

export interface TabHubEndpoint {
  ip: string;
  port: number;
  secure: boolean;
  hubId?: string;
  name?: string;
}

/** Remember THIS window's hub (called on every local connect). */
export function rememberTabActiveHub(endpoint: TabHubEndpoint): void {
  try {
    sessionStorage.setItem(TAB_HUB_KEY, JSON.stringify(endpoint));
  } catch {
    // sessionStorage unavailable — the shared localStorage still applies.
  }
}

/** The hub this window connected to, if it did. */
export function readTabActiveHub(): TabHubEndpoint | null {
  try {
    const raw = sessionStorage.getItem(TAB_HUB_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<TabHubEndpoint> | null;
    const port = Number(parsed?.port);
    if (!parsed?.ip || !Number.isFinite(port) || port <= 0) return null;
    return { ip: parsed.ip, port, secure: parsed.secure === true, hubId: parsed.hubId, name: parsed.name };
  } catch {
    return null;
  }
}

/** Forget this window's hub (e.g. on logout). */
export function clearTabActiveHub(): void {
  try {
    sessionStorage.removeItem(TAB_HUB_KEY);
  } catch {
    // ignore
  }
}

/** The active hub for THIS window: its own marker first, shared storage after. */
export function getActiveHubEndpoint(): TabHubEndpoint | null {
  const tab = readTabActiveHub();
  if (tab) return tab;
  try {
    const ip = localStorage.getItem(ACTIVE_HUB_IP_KEY);
    const port = Number(localStorage.getItem(ACTIVE_HUB_PORT_KEY));
    if (!ip || !Number.isFinite(port) || port <= 0) return null;
    return { ip, port, secure: getStoredActiveHubSecure() };
  } catch {
    return null;
  }
}

/**
 * The hub's own id as THIS window recorded it: `otto_active_hub_key` is
 * `hub:<hubId>` while the window is on a hub, and names the connection
 * (`local:<host>:<port>/api`) when it is not.
 *
 * The app holds **two id spaces for one hub** and they are not
 * interchangeable: the account's own row id (`GET /hubs` → `hubId`, which is
 * what a launcher-driven tab carries and what the tunnel is opened with) and the
 * hub's own id (`→ macAddress`, the id `/api/hub/identity` answers with, which
 * is what a claimed session is filed under here). A caller that needs one of
 * them must say which — and reading a key this app never writes is not a way to
 * get one: `stores/connectionLost.ts` looked up `otto_active_hub_id`, which
 * nothing in this app has ever written, so a lost-session reconnect reached the
 * claim ladder with no id at all and could not open a hub that was live a second
 * earlier (tab `Kolomban`, 28.9.2026).
 */
export function getActiveHubOwnId(): string | null {
  try {
    // THIS window's own marker answers first (see TAB_HUB_KEY above): the
    // shared `otto_active_hub_key` is rewritten by whichever window connected
    // last ("reloading the Rožna tab showed Dev"), so a window that IS on a
    // hub must not read another window's answer out of it. Measured again on
    // the Dev map window (2.10.2026, four hub windows open): cloud-area
    // resolution asked this function, read Rožna S1's shared key, resolved
    // Rožna's cloud row — Dev's panel listed Rožna's area and showed Dev's own
    // area as a read-only mirror ("Upravljano v oblaku", no edit, no move).
    // The marker's `hubId` is whichever spelling its writer held (a local
    // connect files the hub's own id, a launcher-driven tab the account row
    // id); both resolve downstream (`findAccountHubRow` matches either
    // spelling). Only a window that never recorded a marker — nothing on this
    // window has connected yet — falls back to the shared key.
    const marker = readTabActiveHub();
    const own = marker?.hubId?.trim();
    if (own) return own;
    const key = localStorage.getItem('otto_active_hub_key') || '';
    if (!key.startsWith('hub:')) return null;
    return key.slice('hub:'.length).trim() || null;
  } catch {
    return null;
  }
}

/**
 * The ids a lost-session reconnect carries out of the window it is in.
 *
 * One hub, **two id spaces**, and neither is a stand-in for the other: the
 * account's own row id (`GET /hubs` → `hubId`) is what the tunnel is opened with,
 * and the hub's own id (`→ macAddress`, what `/api/hub/identity` answers with) is
 * what a claimed session is filed under on the device. A launcher-driven tab
 * carries the first as its `hubId` (`components/server/ServersSection.vue` →
 * `openServerTab`) and the window's own `otto_active_hub_key` carries the second.
 * Both are handed on: the claim ladder looks the hub up under either spelling
 * (`services/hubLocalFirst.ts` — "both spellings the app may hold are asked"), so
 * a connect is never left holding the one id it cannot use.
 *
 * The measured defect this exists for (tab `Kolomban`, 28.9.2026): the
 * lost-session reconnect read `otto_active_hub_id`, a key **nothing in this app
 * writes**, so its target carried no id at all — the ladder answered
 * `no-account-identity` without dialling the hub, that refusal does not fall back
 * to the tunnel, and the overlay answered a session that had been live a second
 * earlier with "No credential for this hub — join or adopt it". The hub was
 * reachable, the signed-in account owned it, and its pinned `publicId` matched
 * the responder: the only thing missing was the id.
 */
export function lostSessionHubIds(input: {
  tabHubId?: string | null;
  ownHubId?: string | null;
}): { hubId?: string; accountHubId?: string } {
  const tabHubId = (input.tabHubId ?? '').trim();
  const ownHubId = (input.ownHubId ?? '').trim();
  // The tab marker's id first: for a launcher-driven tab that is the account's row
  // id, the one the tunnel needs. The other spelling goes beside it as
  // `accountHubId`, which is the field `HubTarget` keeps for exactly this.
  const hubId = tabHubId || ownHubId || undefined;
  const accountHubId = ownHubId && ownHubId !== hubId ? ownHubId : undefined;
  return { hubId, accountHubId };
}

export function isPrivateOrLocalHostname(hostname: string): boolean {
  const lower = hostname.toLowerCase();

  return (
    lower === 'localhost' ||
    lower === '127.0.0.1' ||
    lower.endsWith('.local') ||
    /^10\./.test(lower) ||
    /^192\.168\./.test(lower) ||
    /^172\.(1[6-9]|2[0-9]|3[0-1])\./.test(lower)
  );
}

function buildElectronLocalApiUrl(hostname: string, port: number | null, pathName = '/api'): string {
  // A `null` port is an address that names none — the cleartext front door — and
  // the `otto-local://` URL then names none either: the ladder walks its
  // portless rungs (the front door first, the legacy ports behind it) instead of
  // being locked to one port. That is also the shape a renderer call arrives in
  // (Chromium drops a port written in this scheme's authority — see
  // electron/localProtocolLadder.ts), so both transports name the same address.
  return `${ELECTRON_LOCAL_PROTOCOL}://${port ? `${hostname}:${port}` : hostname}${pathName}`;
}

export function isElectronLocalApiUrl(apiUrl: string): boolean {
  return apiUrl.startsWith(`${ELECTRON_LOCAL_PROTOCOL}://`);
}

export function toDirectHubApiUrl(apiUrl: string): string {
  if (!isElectronLocalApiUrl(apiUrl)) {
    return apiUrl;
  }

  const parsed = new URL(apiUrl);
  return `${getStoredActiveHubSecure() ? 'https' : 'http'}://${parsed.host}${parsed.pathname}${parsed.search}`;
}

function toElectronLocalApiUrl(apiUrl: string, wsUrl?: string): string {
  if (isElectronLocalApiUrl(apiUrl)) {
    return apiUrl;
  }

  if (/^https?:\/\//i.test(apiUrl)) {
    const parsed = new URL(apiUrl);
    return buildElectronLocalApiUrl(
      parsed.hostname,
      // A port the address spells is the service it names; a cleartext address
      // that spells none means the front door and stays port-less. TLS keeps the
      // port the URL standard gives it — a portless `https://` address is 443,
      // and the ladder must not re-walk the cleartext rungs for one.
      parsed.port ? Number(parsed.port) : parsed.protocol === 'http:' ? null : HUB_TLS_PORT,
      `${parsed.pathname}${parsed.search}`,
    );
  }

  if (apiUrl.startsWith('/')) {
    // Nothing said about the hub's address: the cleartext front door is the
    // honest default now (it is what `ws://<host>` means), and a socket URL that
    // spells a port still carries it.
    const fallbackSocketUrl = wsUrl || 'ws://localhost';
    const socket = new URL(fallbackSocketUrl);
    const port = socket.port ? Number(socket.port) : socket.protocol === 'wss:' ? HUB_TLS_PORT : null;
    return buildElectronLocalApiUrl(socket.hostname, port, apiUrl);
  }

  return apiUrl;
}

export function buildHubApiUrl(hostname: string, port: number, secure?: boolean): string {
  const directApiUrl = `${hubScheme(secure)}://${formatHubAuthority(hostname, port, secure)}/api`;
  return isElectron() ? toElectronLocalApiUrl(directApiUrl) : directApiUrl;
}

/**
 * The scheme a hub endpoint is reached on, as a **three-valued** hint (card
 * `t_cf6ea0e5`):
 *
 *   - `true`  — TLS was measured: `https://…/api` and `wss://…`.
 *   - `false` — cleartext was measured: `http://…/api` and `ws://…`.
 *   - `undefined` — **nothing said**. This is the historical default, and it is
 *     deliberately not the same as `false`:
 *       - `buildHubApiUrl()` stays `http://` (exactly what every build before
 *         this one dialled, so no hub that works today stops working);
 *       - `setActiveHub()` keeps its own measured rule (port 443 IS TLS —
 *         `ws://host:443` can never connect).
 *
 * It is three-valued because that is how it arrives: a hub's mDNS announcement
 * or TXT record may name a scheme, a probe may find one that answers, and a
 * saved connection from an older build carries neither.
 */
export type HubSecureHint = boolean | undefined;

/** The port every hub on this fleet serves TLS on (`nginx`, self-signed). */
export const HUB_TLS_PORT = 443;

/**
 * The hub's **front door**: plain HTTP on the port an address that names none
 * means (card `t_c2446418`, plan `LOCAL-HTTP-AND-CLOUD-ADOPTION.md` §2/C1).
 *
 * The hub's own listener is the product's door now — cleartext, no certificate
 * in front of it (`otto/src/lib/hubPorts.ts` → `PRODUCTION_HTTP_PORT`), and
 * `16161` survives one release as a bound-but-unadvertised alias. So an address
 * written without a port is the front door, and the app writes one without a
 * port instead of spelling `:16161` into it: a development checkout keeps its
 * own front door on `16161` (`otto/.env`), which is why a **measured** port
 * always travels with its scheme and is never replaced by this default.
 */
export const HUB_FRONT_DOOR_PORT = 80;

/**
 * Is this endpoint written without a port — i.e. is the port the one this
 * address already means?
 *
 * Only the cleartext front door qualifies: `http://host` and `http://host:80`
 * are one address, so the port is dropped and the ladder walks the portless
 * rungs (the front door first, the legacy ports behind it). Every other port is
 * **kept**, including `443` on TLS and a cleartext `443` that was measured
 * there: a stored port names a service on that host, and an address that drops
 * it would let a hub request find whatever else answers on the same box
 * (electron/localProtocolLadder.ts, "with an explicit port: only that port").
 */
export function isHubFrontDoorAddress(port: number, secure?: boolean): boolean {
  return hubScheme(secure) === 'http' && port === HUB_FRONT_DOOR_PORT;
}

/** `host` or `host:port`, as an address is written (see `isHubFrontDoorAddress`). */
export function formatHubAuthority(hostname: string, port: number, secure?: boolean): string {
  return isHubFrontDoorAddress(port, secure) ? hostname : `${hostname}:${port}`;
}

export function hubScheme(secure?: boolean): 'http' | 'https' {
  return secure === true ? 'https' : 'http';
}

export function hubWsScheme(secure?: boolean): 'ws' | 'wss' {
  return secure === true ? 'wss' : 'ws';
}

/** The socket URL of a hub endpoint — the same scheme decision as `buildHubApiUrl`. */
export function buildHubWsUrl(hostname: string, port: number, secure?: boolean): string {
  return `${hubWsScheme(secure)}://${formatHubAuthority(hostname, port, secure)}`;
}

/**
 * Stable per-hub key used to remember per-hub UI state (last viewed room /
 * settings page / dashboard). Uses the cloud hub id for /h/:hubId routes and a
 * normalized local endpoint (host:port, loopback aliases collapsed) for direct
 * hub connections, so each hub keeps its own memory.
 */
export function getHubSessionKey(routeHubId?: string | string[] | null): string {
  return getHubSessionKeys(routeHubId)[0];
}

/**
 * All candidate per-hub keys for the current connection, most specific first.
 *
 * Order matters: for LOCAL connections the normalized endpoint key
 * (`local:host:port/api`) is deterministic and always freshly written on every
 * room/settings/dashboard visit, so it is returned BEFORE the mutable
 * `otto_active_hub_key` hub key. The hub key can be stale — it is rewritten
 * whenever the user switches hubs or visits the Servers page — so if it came
 * first, a stale hub value (e.g. an old room for THIS hub) would win over the
 * fresh local value and the app would look "stuck" restoring the same room.
 *
 * For cloud (WebRTC) routes the route hubId key (`hub:<cloudHubId>`) is still
 * first, which is correct because WebRTC sessions have no meaningful local
 * endpoint.
 *
 * A no-port variant of each local endpoint key (`local:host/api` without
 * `:port`) is also included as a fallback: older builds wrote per-hub state
 * under the no-port form, so reads must still find that data instead of
 * falling through to a (possibly stale) hub key.
 */
export function getHubSessionKeys(routeHubId?: string | string[] | null): string[] {
  const keys: string[] = [];
  const hubId = Array.isArray(routeHubId) ? routeHubId[0] : routeHubId;

  // 1. First priority: Use route-scoped hubId param if available (cloud WebRTC routes)
  if (hubId && !keys.includes(`hub:${hubId}`)) {
    keys.push(`hub:${hubId}`);
  }

  // 2. Second priority: Use active hubId from localStorage (upgraded hub identity)
  // This is highly stable across both local LAN and WebRTC connections, unaffected by
  // IP changes, port mappings, or custom protocol URL parsing quirks.
  try {
    const stored = localStorage.getItem('otto_active_hub_key');
    if (stored && stored.startsWith('hub:') && stored !== 'hub:undefined') {
      if (!keys.includes(stored)) {
        keys.push(stored);
      }
    }
  } catch {
    // ignore
  }

  // 3. Third priority fallbacks: Local endpoints (with and without port) for transition state,
  // back-compatibility, and manual connection setup before identity probing completes.
  // The scheme-aware shape comes from `localHubSessionKeys` — one place decides it.
  try {
    const apiUrl = getRuntimeConfig().apiUrl;
    for (const key of localHubSessionKeys(apiUrl)) {
      if (!keys.includes(key)) keys.push(key);
    }
    // A TLS endpoint also answers to the cleartext spelling of its own address
    // (`local:host:port/api`): a device credential, a token or a remembered
    // position written by a build that did not yet carry the scheme belongs to
    // this same hub, and losing it would re-enrol the device. This reader is the
    // permissive one on purpose (it also walks `otto_active_hub_key`); the
    // connection-scoped `getHubOwnKeys()` is not, and does not do this.
    if (/^https:/i.test(toDirectHubApiUrl(apiUrl))) {
      for (const key of localHubSessionKeys(apiUrl.replace(/^https:/i, 'http:'))) {
        if (!keys.includes(key)) keys.push(key);
      }
    }
  } catch {
    // ignore
  }

  // 4. Stored active hub key fallback (if it contains older local: IP-based formats)
  try {
    const stored = localStorage.getItem('otto_active_hub_key');
    if (stored && !keys.includes(stored)) {
      keys.push(stored);
    }
  } catch {
    // ignore
  }

  if (keys.length === 0) return ['local'];
  return keys;
}

/**
 * Session keys that identify ONE connection — the hub id when it is known,
 * otherwise the LAN endpoint the client is pointed at right now.
 *
 * `getHubSessionKeys()` above deliberately falls back to `otto_active_hub_key`
 * and to legacy LAN keys so that old data is still found. That is right for
 * credentials and tokens, but it is WRONG for anything that stores a *position*
 * per hub (`lastHubPath:*`, `lastViewedRoomsPage:*`): a reader that takes the
 * first non-empty value hands back the previously visited hub's page, and an
 * id-bearing path (`users/<id>`, `integrations/<id>`) then opens against the
 * wrong hub and renders "not found" / empty.
 *
 * Contract: the returned list contains ONLY keys of the connection named by the
 * arguments — add nothing that could belong to another hub.
 */
export function getHubOwnKeys(opts: { hubId?: string | string[] | null; apiUrl?: string | null } = {}): string[] {
  const keys: string[] = [];
  const hubId = Array.isArray(opts.hubId) ? opts.hubId[0] : opts.hubId;

  // Cloud/WebRTC connection: the hub id is the identity, nothing else qualifies.
  if (hubId) {
    keys.push(`hub:${hubId}`);
    return keys;
  }

  // LAN / Electron-local connection: only the endpoint keys of THIS apiUrl.
  // The router writes every key of the connection it is on, so a page visited
  // on a LAN hub is stored under this endpoint key as well as the hub key.
  try {
    const raw = opts.apiUrl ?? getRuntimeConfig().apiUrl;
    for (const key of localHubSessionKeys(raw)) {
      if (!keys.includes(key)) keys.push(key);
    }
  } catch {
    // ignore
  }

  // Legacy installs stored the endpoint under the active-hub key in a `local:`
  // form — that is still THIS connection, a `hub:` form is not.
  try {
    const stored = localStorage.getItem('otto_active_hub_key');
    if (stored && stored.startsWith('local:') && !keys.includes(stored)) {
      keys.push(stored);
    }
  } catch {
    // ignore
  }

  if (keys.length === 0) return ['local'];
  return keys;
}

/**
 * The `local:` session keys of a LAN endpoint, most specific first — the one
 * place that decides what a hub endpoint is called in storage.
 *
 * Cleartext keeps the historical `local:host:port/api` spelling: every install
 * in the field holds its per-hub state under it, and `hubResumePath.test.ts`
 * pins the shape. A **TLS** endpoint carries the scheme instead
 * (`local:https://host:port/api`), because a hub reached over https and http is
 * two connections — the same address on a different transport — and a key that
 * cannot tell them apart hands one session's credential or position to the
 * other (card `t_cf6ea0e5`).
 *
 * The no-port variant of whichever shape the endpoint has is included for
 * builds that wrote the key without a port.
 */
export function localHubSessionKeys(apiUrl: string): string[] {
  const normalized = toDirectHubApiUrl(apiUrl).replace(/\/+$/, '');
  const tls = /^https:/i.test(normalized);
  const prefix = tls ? 'local:https://' : 'local:';
  const hostPath = normalized.replace(/^[a-z][a-z0-9+.-]*:\/\//i, '').replace(/(localhost|::1)/i, '127.0.0.1');
  const keys = [`${prefix}${hostPath}`];
  const noPort = hostPath.replace(/:\d+(?=\/|$)/, '');
  if (noPort !== hostPath) keys.push(`${prefix}${noPort}`);
  return keys;
}

export function getDirectHubOrigin(apiUrl?: string): string {
  const resolvedApiUrl = apiUrl || getRuntimeConfig().apiUrl;
  return toDirectHubApiUrl(resolvedApiUrl).replace(/\/api\/?$/, '');
}

export function normalizeHubApiUrl(apiUrl: string): string {
  return toDirectHubApiUrl(apiUrl).replace(/\/+$/, '');
}

export function rewriteElectronLocalRequestUrl(url: string): string {
  if (!isElectron()) {
    return url;
  }

  const currentConfig = config;

  if (url.startsWith('/api')) {
    if (currentConfig?.mode === 'electron' && currentConfig.apiUrl) {
      return `${currentConfig.apiUrl}${url.slice('/api'.length)}`;
    }
    return url;
  }

  if (url.startsWith('file://')) {
    const parsed = new URL(url);
    if (parsed.pathname.startsWith('/api') && currentConfig?.mode === 'electron' && currentConfig.apiUrl) {
      return `${currentConfig.apiUrl}${parsed.pathname.slice('/api'.length)}${parsed.search}`;
    }
    return url;
  }

  try {
    const parsed = new URL(url);
    if (
      (parsed.protocol === 'http:' || parsed.protocol === 'https:') &&
      parsed.pathname.startsWith('/api') &&
      isPrivateOrLocalHostname(parsed.hostname)
    ) {
      return toElectronLocalApiUrl(url);
    }
  } catch {
    return url;
  }

  return url;
}

/**
 * Detect if otto-ui is running embedded inside otto-web.
 * otto-web serves the UI at /app/* — check the URL pathname.
 */
export function isOttoWebEmbedded(): boolean {
  try {
    // Only the embedded base itself counts. otto-web serves this SPA under /app,
    // but a route of our own that merely starts with those letters
    // (/app-settings/...) is not embedded: treating it as one made the loader
    // fetch /app/config.json (404 HTML), fail to parse it and fall back to the
    // broken relative /api defaults.
    const path = window.location.pathname;
    return path === '/app' || path.startsWith('/app/');
  } catch {
    return false;
  }
}

// ── A hub serving the app itself, on a hostname nobody can call "local" ─────
//
// Card `t_6d25f68a` (D10's follow-up). The demo house serves the app's own HTML
// from the hub (`public/`, `src/server/spa.ts`), so the page a guest is handed
// to runs on the hub's own origin — but on the demo box that origin is
// `https://demo.torik.ai`, and `mode: "auto"` asks `isLocalMode()`, a hostname
// test. A public name is not a localhost name, so the UI resolved CLOUD,
// pointed `apiUrl` at `https://torik.ai/api`, ignored the bootstrap keys the
// welcome page had just written (`restoreActiveHubFromStorage` returns early
// for cloud mode), and the guest landed in the cloud launcher instead of the
// house.
//
// What such an origin cannot hide is that it IS a hub: `GET /api/hub/identity`
// is public by design (F11), it answers on the same origin the page came from,
// and nothing else this app runs on answers it — the cloud 404s it (measured
// 7.10.2026 on torik.ai and app.torik.ai, both `404 text/html`), a static host
// answers HTML, and only a real hub answers the id + key pair below. So the
// probe is the deployment-independent way to ask "was I served by a hub?",
// which is also the only question `isLocalMode()` cannot answer: the ROUTER
// asks the same thing from a hostname (`router/index.ts` — the `/` redirect),
// and seeding the box's `config.json` would not have reached it.

/** How long the own-origin probe may take before the answer is "not a hub". */
export const HUB_SERVED_PROBE_TIMEOUT_MS = 2500;

/** The fields of `GET /api/hub/identity` this file reads. */
export interface HubIdentityAnswer {
  publicId: string;
  publicKey: string;
  hubId?: string | null;
  name?: string;
}

/**
 * Is this a hub's identity answer? `publicId`'s shape is F9's rule, written out
 * here rather than imported from `utils/hubIdentity`: that module imports THIS
 * one (`HUB_TLS_PORT`, `HUB_FRONT_DOOR_PORT`), and a cycle on the boot path is
 * not worth one regex. Anything that cannot present both an id and a key is not
 * a hub — an HTML 404 body, a cloud route, a static host.
 */
export function isHubIdentityAnswer(body: unknown): body is HubIdentityAnswer {
  if (!body || typeof body !== 'object') return false;
  const answer = body as { publicId?: unknown; publicKey?: unknown };
  const publicId = typeof answer.publicId === 'string' ? answer.publicId.trim().toLowerCase() : '';
  return /^[0-9a-f]{16}$/.test(publicId) && typeof answer.publicKey === 'string' && answer.publicKey.length > 0;
}

/**
 * Does THIS page's own origin answer as a hub? The probe `loadRuntimeConfig`
 * asks before it settles for cloud mode (see the block above). Answers `false`
 * for everything that is not a hub — a failed request, a timeout, a 404, an
 * HTML body — because the caller's only alternative is the behaviour every
 * public hostname had before this existed.
 *
 * `fetchImpl` is injectable for tests; the real caller passes nothing.
 */
export async function isHubServedOrigin(
  fetchImpl: typeof fetch | null = null,
  timeoutMs: number = HUB_SERVED_PROBE_TIMEOUT_MS,
): Promise<boolean> {
  if (typeof window === 'undefined') return false;
  const doFetch = fetchImpl ?? (typeof fetch === 'function' ? fetch : null);
  if (!doFetch) return false;

  const controller = typeof AbortController === 'function' ? new AbortController() : null;
  const timer: ReturnType<typeof setTimeout> | null = controller
    ? setTimeout(() => controller.abort(), timeoutMs)
    : null;
  try {
    const response = await doFetch(`${window.location.origin}/api/hub/identity`, {
      headers: { Accept: 'application/json' },
      signal: controller ? controller.signal : undefined,
    });
    if (!response.ok) return false;
    return isHubIdentityAnswer(await response.json());
  } catch {
    return false;
  } finally {
    if (timer !== null) clearTimeout(timer);
  }
}

function restoreActiveHubFromStorage(current: RuntimeConfig): RuntimeConfig {
  try {
    if (typeof window === 'undefined' || current.mode === 'cloud') {
      return current;
    }

    // Prefer this window's own hub — the shared localStorage value is the LAST
    // hub any tab connected to, which would hijack a reloaded hub tab.
    const endpoint = getActiveHubEndpoint();
    if (!endpoint) return current;
    const { ip, port } = endpoint;
    // 443 IS TLS even when the connect target carried no secure flag (the flag
    // is not part of the tab-connect payload): rebuilding `ws://…:443` left the
    // reloaded tab's socket dead, and the stale-socket guard then logged the
    // session out and bounced the tab to /home.
    const useTls = endpoint.secure === true || port === HUB_TLS_PORT;

    return {
      ...current,
      // Keep the original mode (e.g. 'capacitor') so that socket.ts uses the
      // full wsUrl rather than '/' — on Android Capacitor '/' points to the
      // device itself, not the hub.
      apiUrl:
        current.mode === 'electron'
          ? buildHubApiUrl(ip, port, useTls)
          : `${hubScheme(useTls)}://${formatHubAuthority(ip, port, useTls)}/api`,
      wsUrl: buildHubWsUrl(ip, port, useTls),
    };
  } catch {
    return current;
  }
}

/**
 * Detect if we're running inside Capacitor (native Android/iOS)
 */
export async function isCapacitorAsync(): Promise<boolean> {
  try {
    // @capacitor/core sets Capacitor.isNativePlatform() to true on device
    const { Capacitor } = await import('@capacitor/core');
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
}

// Sync version for critical paths (checks window object)
export function isCapacitor(): boolean {
  try {
    // In Capacitor, the window.Capacitor object is available immediately
    const capacitor = window.Capacitor;
    return !!capacitor?.isNativePlatform && capacitor.isNativePlatform();
  } catch {
    return false;
  }
}

/**
 * Detect if we're running in local mode
 * Local mode: accessing via hub.local, localhost, or local IP
 */
export function isLocalMode(): boolean {
  const hostname = window.location.hostname;

  // Check for local hostnames
  if (
    hostname === 'localhost' ||
    hostname === 'hub.local' ||
    hostname.endsWith('.local') ||
    hostname === '127.0.0.1' ||
    hostname.startsWith('192.168.') ||
    hostname.startsWith('10.') ||
    hostname.startsWith('172.16.') ||
    hostname.startsWith('172.17.') ||
    hostname.startsWith('172.18.') ||
    hostname.startsWith('172.19.') ||
    hostname.startsWith('172.20.') ||
    hostname.startsWith('172.21.') ||
    hostname.startsWith('172.22.') ||
    hostname.startsWith('172.23.') ||
    hostname.startsWith('172.24.') ||
    hostname.startsWith('172.25.') ||
    hostname.startsWith('172.26.') ||
    hostname.startsWith('172.27.') ||
    hostname.startsWith('172.28.') ||
    hostname.startsWith('172.29.') ||
    hostname.startsWith('172.30.') ||
    hostname.startsWith('172.31.')
  ) {
    return true;
  }

  return false;
}

/**
 * Load runtime configuration
 * Fetches config.json and determines mode
 */
export async function loadRuntimeConfig(): Promise<RuntimeConfig> {
  if (config) {
    return config;
  }

  try {
    // Resolve config.json from the app root (origin, or the /app base when
    // embedded) — NOT relative to the current route. Otherwise a full reload
    // on a deep path (e.g. /h/{hubId}/dashboards/{id}) fetches
    // /h/.../dashboards/config.json (404 HTML) and falls back to broken
    // defaults (relative /api), breaking all hub API calls.
    const basePath = isOttoWebEmbedded() ? '/app' : '';
    const configUrl = new URL(`${basePath}/config.json`, window.location.origin).toString();
    const response = await fetch(configUrl);
    const configFile = await response.json();

    // Determine mode priority: Capacitor > Electron > config-file / auto-detect
    const isCapacitorApp = isCapacitor();
    const isElectronApp = isElectron();
    let mode: RuntimeConfig['mode'] = 'cloud';
    /**
     * The page was served BY a hub, on an origin the hostname test cannot see as
     * one (`isHubServedOrigin` above). `auto` + "not a localhost name" used to
     * mean cloud, which sent a guest of the demo house — served from
     * `demo.torik.ai` — into the cloud launcher instead of the house.
     */
    let hubServed = false;

    if (isCapacitorApp) {
      mode = 'capacitor';
    } else if (isElectronApp) {
      mode = 'electron';
    } else if (configFile.mode === 'auto') {
      mode = isLocalMode() ? 'local' : 'cloud';
      // Only where the answer can still be wrong: a localhost name has already
      // resolved, and the otto-web-embedded surface (`/app`) is the cloud's own
      // page whatever its origin answers.
      if (mode === 'cloud' && !isOttoWebEmbedded()) {
        hubServed = await isHubServedOrigin();
        if (hubServed) {
          console.log('[RuntimeConfig] This origin answers as a hub — hub-served UI, local mode');
          mode = 'local';
        }
      }
    } else {
      mode = configFile.mode;
    }

    // Build config based on mode
    if (mode === 'capacitor') {
      // In Capacitor mode, apiUrl/wsUrl start empty — set dynamically after hub selection
      config = {
        mode: 'capacitor',
        apiUrl: '',
        wsUrl: '',
        cloudApiUrl: configFile.cloud?.apiUrl || 'https://torik.ai/api',
        cloudWsUrl: configFile.cloud?.wsUrl || 'wss://torik.ai',
      };
    } else if (mode === 'electron' || mode === 'local') {
      // A hub-served UI's socket is its own origin: the shipped
      // `ws://localhost:16161` names a development front door, not a public
      // hostname's TLS front (where `:16161` is not even listening).
      const wsUrl = hubServed
        ? `${hubWsScheme(window.location.protocol === 'https:')}://${window.location.host}`
        : configFile.local.wsUrl.replace('localhost', window.location.hostname || 'localhost');
      // …and so is its API — a relative base through whatever terminates the
      // connection in front of the hub, which is exactly what the shipped
      // `local.apiUrl` already carries.
      const apiUrl = hubServed ? '/api' : configFile.local.apiUrl;
      config = {
        mode: mode as 'local' | 'electron',
        apiUrl: mode === 'electron' ? toElectronLocalApiUrl(apiUrl, wsUrl) : apiUrl,
        wsUrl,
        cloudApiUrl: configFile.cloud?.apiUrl || 'https://torik.ai/api',
        cloudWsUrl: configFile.cloud?.wsUrl || 'wss://torik.ai',
        hubServed: hubServed || undefined,
      };
    } else {
      // Cloud mode — detect if embedded in otto-web
      const embedded = isOttoWebEmbedded();
      config = {
        mode: 'cloud',
        apiUrl: embedded || !configFile.cloud.apiUrl ? '/api' : configFile.cloud.apiUrl,
        wsUrl: embedded || !configFile.cloud.wsUrl ? '' : configFile.cloud.wsUrl,
        cloudApiUrl: embedded || !configFile.cloud.apiUrl ? '/api' : configFile.cloud.apiUrl,
        cloudWsUrl: embedded || !configFile.cloud.wsUrl ? '' : configFile.cloud.wsUrl,
        embeddedInOttoWeb: embedded,
      };
    }

    // A hub-served origin skips the storage restore (card `t_6d25f68a`): its
    // own origin IS the hub, and storage on this origin can only ever have been
    // written for it — the welcome page's bootstrap keys included. Those keys
    // are still read where they are spent (`getActiveHubEndpoint` /
    // `readTabActiveHub` in the auth store), so the guest's token is found.
    if (!config.hubServed) {
      config = restoreActiveHubFromStorage(config);
    }

    console.log(`[RuntimeConfig] Mode: ${config.mode}`);
    console.log(`[RuntimeConfig] API URL: ${config.apiUrl}`);
    console.log(`[RuntimeConfig] WS URL: ${config.wsUrl}`);

    return config;
  } catch (error) {
    console.error('[RuntimeConfig] Failed to load config, using defaults:', error);

    // Fallback to auto-detection
    const isCapacitorApp = isCapacitor();
    const isElectronApp = isElectron();
    let mode: RuntimeConfig['mode'] = 'cloud';

    if (isCapacitorApp) {
      mode = 'capacitor';
    } else if (isElectronApp) {
      mode = 'electron';
    } else if (isLocalMode()) {
      mode = 'local';
    }

    if (mode === 'capacitor') {
      config = {
        mode: 'capacitor',
        apiUrl: '',
        wsUrl: '',
        cloudApiUrl: 'https://torik.ai/api',
        cloudWsUrl: 'wss://torik.ai',
      };
    } else {
      const embedded = isOttoWebEmbedded();
      // No port: the fallback address names the hub's **front door** (card
      // `t_c2446418` — a URL that spells no port means 80), and the ladder
      // carries the legacy rungs behind it, so a hub still on `16161` answers
      // too. `ws://host` is the same address the app writes for a hub it
      // measured on the front door.
      const fallbackWsUrl = `ws://${window.location.hostname || 'localhost'}`;
      const isCloudMode = mode === 'cloud' || (!isCapacitorApp && !isElectronApp && mode !== 'local');
      config = {
        mode,
        apiUrl:
          mode === 'electron'
            ? toElectronLocalApiUrl('/api', fallbackWsUrl)
            : mode === 'local'
              ? '/api'
              : isCloudMode
                ? '/api'
                : 'https://torik.ai/api',
        wsUrl: mode === 'local' || mode === 'electron' ? fallbackWsUrl : isCloudMode ? '' : 'wss://torik.ai',
        cloudApiUrl: isCloudMode ? '/api' : 'https://torik.ai/api',
        cloudWsUrl: isCloudMode ? '' : 'wss://torik.ai',
        embeddedInOttoWeb: embedded,
      };
    }

    config = restoreActiveHubFromStorage(config);
    return config;
  }
}

/**
 * Get current runtime config (must be loaded first)
 */
export function getRuntimeConfig(): RuntimeConfig {
  if (!config) {
    throw new Error('Runtime config not loaded. Call loadRuntimeConfig() first.');
  }
  return config;
}

/**
 * Dynamically reconfigure apiUrl/wsUrl after the user selects a local hub.
 * Used by the connection screen (Capacitor + Electron manual connect).
 *
 * `secure` is the three-valued hint (`HubSecureHint`): `true`/`false` are
 * **measured** schemes — an address that answered over TLS is dialled over TLS,
 * and one that answered cleartext is dialled cleartext, even on 443 — while
 * `undefined` is "nothing said", which keeps this function's own historical rule
 * (`443` IS TLS: `ws://host:443` can never connect).
 *
 * `identity` is the hub's own id/name when the caller holds one — the claim's
 * adoption (`services/hubJoinFlow.ts` → `adoptLink`) and the adoption flow both
 * do. The per-tab marker is this window's record of the hub it is on, and a
 * re-pin of the same address used to drop the id the connect had just written:
 * after a claim, the marker named no hub at all, so a lost-session reconnect in
 * that window fell back to the **shared** `otto_active_hub_key` — which is
 * whichever tab wrote it last, i.e. another hub's (card `t_f000e669`). The
 * writer that knows the hub names it.
 */
export function setActiveHub(
  ip: string,
  port: number,
  secure?: boolean,
  identity?: { hubId?: string | null; name?: string | null },
): void {
  if (!config) {
    throw new Error('Runtime config not loaded. Call loadRuntimeConfig() first.');
  }

  const useTls = typeof secure === 'boolean' ? secure : port === HUB_TLS_PORT;

  try {
    localStorage.setItem(ACTIVE_HUB_IP_KEY, ip);
    localStorage.setItem(ACTIVE_HUB_PORT_KEY, String(port));
    localStorage.setItem(ACTIVE_HUB_SECURE_KEY, useTls ? 'true' : 'false');
  } catch {
    // Ignore storage errors; runtime config still updates in-memory.
  }

  // This window's own hub (per-tab — see TAB_HUB_KEY above). The id/name the
  // caller holds are written with it: the answer to a lost session is filed
  // under the hub's own id, and the shared key belongs to another window.
  rememberTabActiveHub({
    ip,
    port,
    secure: useTls,
    hubId: identity?.hubId?.trim() || undefined,
    name: identity?.name?.trim() || undefined,
  });

  config.apiUrl =
    config.mode === 'electron'
      ? buildHubApiUrl(ip, port, useTls)
      : `${hubScheme(useTls)}://${formatHubAuthority(ip, port, useTls)}/api`;
  config.wsUrl = buildHubWsUrl(ip, port, useTls);
  // Remember the hub key for per-hub UI state (upgraded to hub:<hubId> once
  // the hub's identity is known after login / WebRTC connect).
  try {
    localStorage.setItem('otto_active_hub_key', getHubSessionKey());
  } catch {
    // ignore
  }
  // Treat as local once a hub is selected — any mode connecting directly to
  // a hub IP should switch to local so routedFetch bypasses WebRTC checks.
  if (config.mode === 'capacitor' || config.mode === 'cloud') {
    config.mode = 'local';
  }
  console.log(`[RuntimeConfig] Active hub set → API: ${config.apiUrl}  WS: ${config.wsUrl}`);
}

/**
 * Check if running in Electron
 */
export function isElectron(): boolean {
  return !(typeof window === 'undefined') && !!window.electron;
}

/**
 * Check if running in local mode (includes capacitor after hub selection)
 */
export function isLocal(): boolean {
  return config?.mode === 'local' || config?.mode === 'electron';
}

/**
 * The flavor the onboarding decision tables take, read
 * synchronously: `getRuntimeConfig().mode`, with the platform checks as the
 * fallback for the window before the config has loaded — exactly the priority
 * `loadRuntimeConfig()` resolves them in (Capacitor > Electron > config/origin).
 *
 * It lives here rather than beside the tables (`config/onboarding.ts`) because
 * that file is a pure decision table the Node test runner loads directly and must
 * not drag the browser-side config loader in. A view that renders *from* a table
 * has to know the mode on its first paint — a template that waits for a promise
 * shows the wrong affordance first — so it reads this one, and
 * `onboarding.ts`'s async wrapper delegates here.
 */
export function readRuntimeFlavor(): RuntimeConfig['mode'] {
  try {
    return getRuntimeConfig().mode;
  } catch {
    if (isCapacitor()) return 'capacitor';
    if (isElectron()) return 'electron';
    return isLocalMode() ? 'local' : 'cloud';
  }
}

/**
 * Check if running in cloud mode
 */
export function isCloud(): boolean {
  return config?.mode === 'cloud';
}

/**
 * Get full backend URL (for static files that don't go through /api proxy)
 */
export function getBackendUrl(): string {
  if (!config) {
    throw new Error('Runtime config not loaded. Call loadRuntimeConfig() first.');
  }

  // In local mode with relative apiUrl, construct full URL
  if ((config.mode === 'local' || config.mode === 'electron') && config.apiUrl.startsWith('/')) {
    // The hub's port is whatever the socket URL spells (e.g. `ws://localhost:16161`
    // → 16161). A socket URL that spells none names the **front door** — a
    // port-less address is 80 now, not the legacy raw port (card `t_c2446418`),
    // so the constructed URL stays port-less as well.
    const wsUrlMatch = config.wsUrl.match(/:([0-9]+)(?=\/|$)/);
    const authority = `${window.location.hostname || 'localhost'}${wsUrlMatch ? `:${wsUrlMatch[1]}` : ''}`;
    return `${hubScheme(config.wsUrl.startsWith('wss:'))}://${authority}`;
  }

  // In cloud mode or with absolute apiUrl, use apiUrl
  return config.apiUrl;
}
